import React, { useEffect, useRef, useState } from 'react';
import { DataLayerId, SupplyShedDriveTime } from '../../lib/types';
import { useForestOwnership } from '../../hooks/useForestOwnership';
import { useAgbTimeSeries } from '../../hooks/useAgbTimeSeries';
import { useStandDensity } from '../../hooks/useStandDensity';
import { useWildfireHazardPotential } from '../../hooks/useWildfireHazardPotential';
import { useSpeciesStats } from '../../hooks/useSpeciesStats';
import { usePerCrownStats } from '../../hooks/usePerCrownStats';
import { isPerCrownAvailable } from '../../hooks/usePerCrownNativeWindow';

interface ShedSummaryCardProps {
  millCode: string | null;
  driveTime: SupplyShedDriveTime;
  selectedLayer: DataLayerId | null;
  onLayerChange: (layer: DataLayerId) => void;
}

/**
 * Six-to-seven tile "at a glance" summary for the active supply shed.
 * Composes the existing per-layer stats hooks — no new precompute, no
 * new S3 fetches beyond what the corresponding panels would themselves
 * fetch when the user picks each layer. Each tile is clickable: clicking
 * a stat selects that stat's source layer in the picker.
 *
 * The card auto-collapses to a closed header strip the first time the
 * user picks a layer (reclaims panel space for the layer's own
 * content); a chevron in the header lets the user re-expand at will.
 *
 * Tree-count tile is gated to sheds with per-crown coverage (Sierra
 * Nevada 60-min only at v1.2). All other sheds get the 6-tile layout.
 */
export const ShedSummaryCard: React.FC<ShedSummaryCardProps> = ({
  millCode,
  driveTime,
  selectedLayer,
  onLayerChange,
}) => {
  const [expanded, setExpanded] = useState(true);
  // One-shot auto-collapse the first time the user picks a layer.
  // After that, manual toggle wins — picking a different layer doesn't
  // re-collapse a card the user has explicitly re-expanded.
  const autoCollapsedRef = useRef(false);
  const prevLayerRef = useRef(selectedLayer);
  useEffect(() => {
    if (
      !autoCollapsedRef.current &&
      prevLayerRef.current === null &&
      selectedLayer !== null
    ) {
      autoCollapsedRef.current = true;
      setExpanded(false);
    }
    prevLayerRef.current = selectedLayer;
  }, [selectedLayer]);

  const showTreeCount = isPerCrownAvailable(millCode, driveTime);

  return (
    <section className="border border-figma-lime/20 rounded-figma-panel bg-figma-card/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-figma-panel/40 transition-smooth"
      >
        <span className="text-[11px] font-roboto font-bold uppercase tracking-[0.16em] text-figma-ink-mute">
          At a glance
        </span>
        <Chevron expanded={expanded} />
      </button>

      {expanded && (
        <div
          className="px-3 pb-3 grid grid-cols-2 gap-2"
          data-testid="shed-summary-grid"
        >
          {showTreeCount && (
            <TreeCountCtaTile
              millCode={millCode}
              driveTime={driveTime}
              onClick={() => onLayerChange('tree_crown_segmentation')}
            />
          )}
          <ForestedAcresTile
            millCode={millCode}
            driveTime={driveTime}
            onClick={() => onLayerChange('canopy_height')}
          />
          <PrivatePctTile
            millCode={millCode}
            driveTime={driveTime}
            onClick={() => onLayerChange('forest_ownership')}
          />
          <MeanAgbTile
            millCode={millCode}
            driveTime={driveTime}
            onClick={() => onLayerChange('aboveground_biomass')}
          />
          <MeanRdTile
            millCode={millCode}
            driveTime={driveTime}
            onClick={() => onLayerChange('stand_density')}
          />
          <HighFireRiskTile
            millCode={millCode}
            driveTime={driveTime}
            onClick={() => onLayerChange('fire_risk')}
          />
          <DominantSpeciesTile
            millCode={millCode}
            driveTime={driveTime}
            onClick={() => onLayerChange('species_diversity')}
          />
        </div>
      )}
    </section>
  );
};

// ────── Generic tile shell ──────────────────────────────────────────────

interface StatTileProps {
  label: string;
  value: string | null;
  unit?: string;
  isLoading: boolean;
  hasError: boolean;
  onClick: () => void;
}

// Single tile: small mono uppercase label, primary value, optional small
// unit suffix. Loading state shows a skeleton bar instead of the value;
// error / no-data states render an em-dash so the layout doesn't
// reflow. Clickable button → selects the source layer.
const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  unit,
  isLoading,
  hasError,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex flex-col gap-1 px-3 py-2.5 bg-figma-card border border-figma-lime/15 hover:border-figma-mint/60 focus:outline-none focus-visible:border-figma-mint rounded transition-smooth text-left min-h-[68px]"
  >
    <span className="text-[10px] font-roboto-mono uppercase tracking-[0.10em] text-figma-ink-mute leading-tight">
      {label}
    </span>
    {isLoading ? (
      <SkeletonBar />
    ) : hasError || value === null ? (
      <span className="text-[15px] font-roboto font-bold text-figma-ink-mute">—</span>
    ) : (
      <span className="text-[15px] font-roboto font-bold text-figma-text leading-tight">
        {value}
        {unit && (
          <span className="ml-1 text-[11px] font-roboto font-normal text-figma-ink-mute">
            {unit}
          </span>
        )}
      </span>
    )}
  </button>
);

const SkeletonBar: React.FC = () => (
  <span
    className="block h-[18px] w-16 bg-figma-ink-mute/20 rounded animate-pulse"
    aria-hidden="true"
  />
);

const Chevron: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    width="10"
    height="6"
    viewBox="0 0 10 6"
    fill="none"
    className={`flex-shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`}
    aria-hidden="true"
  >
    <path
      d="M1 1l4 4 4-4"
      stroke="#46ce85"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ────── Per-stat tiles ──────────────────────────────────────────────────

interface TileFetchProps {
  millCode: string | null;
  driveTime: SupplyShedDriveTime;
  onClick: () => void;
}

const compactNumber = (n: number): string =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`
      : `${Math.round(n)}`;

const ForestedAcresTile: React.FC<TileFetchProps> = ({ millCode, driveTime, onClick }) => {
  const { data, isLoading, error } = useForestOwnership(millCode, driveTime);
  const value = data ? compactNumber(data.forestAcres) : null;
  return (
    <StatTile
      label="Forested acres"
      value={value}
      unit="acres"
      isLoading={isLoading}
      hasError={!!error}
      onClick={onClick}
    />
  );
};

const PrivatePctTile: React.FC<TileFetchProps> = ({ millCode, driveTime, onClick }) => {
  const { data, isLoading, error } = useForestOwnership(millCode, driveTime);
  let value: string | null = null;
  if (data && data.forestAcres > 0) {
    const private_ = data.classAcres.family + data.classAcres.corporate;
    value = `${Math.round((private_ / data.forestAcres) * 100)}%`;
  }
  return (
    <StatTile
      label="Private timberland"
      value={value}
      isLoading={isLoading}
      hasError={!!error}
      onClick={onClick}
    />
  );
};

const MeanAgbTile: React.FC<TileFetchProps> = ({ millCode, driveTime, onClick }) => {
  const { data, isLoading, error } = useAgbTimeSeries(millCode, driveTime);
  let value: string | null = null;
  if (data && data.points.length > 0) {
    value = data.points[data.points.length - 1].avgDensity.toFixed(1);
  }
  return (
    <StatTile
      label="Above-ground biomass"
      value={value}
      unit="Mg/ha"
      isLoading={isLoading}
      hasError={!!error}
      onClick={onClick}
    />
  );
};

const MeanRdTile: React.FC<TileFetchProps> = ({ millCode, driveTime, onClick }) => {
  const { data, isLoading, error } = useStandDensity(millCode, driveTime);
  const value = data ? data.latest.meanRd.toFixed(2) : null;
  return (
    <StatTile
      label="Relative density"
      value={value}
      isLoading={isLoading}
      hasError={!!error}
      onClick={onClick}
    />
  );
};

const HighFireRiskTile: React.FC<TileFetchProps> = ({ millCode, driveTime, onClick }) => {
  const { data, isLoading, error } = useWildfireHazardPotential(millCode, driveTime);
  // "High fire risk" = sum of the high + very_high WHP class fractions of
  // burnable shed area. Both classes capture stands where active fuel-
  // reduction is most defensible; reporting their combined share gives
  // the at-a-glance "what fraction is in trouble" answer that the
  // dominant-class label can't (a 60% Moderate shed and a 60% Very-High
  // shed read very differently to forest staff).
  let value: string | null = null;
  if (data) {
    const high = data.classes.find((c) => c.class === 'high')?.fraction ?? 0;
    const veryHigh = data.classes.find((c) => c.class === 'very_high')?.fraction ?? 0;
    value = `${Math.round((high + veryHigh) * 100)}%`;
  }
  return (
    <StatTile
      label="High fire risk"
      value={value}
      isLoading={isLoading}
      hasError={!!error}
      onClick={onClick}
    />
  );
};

const DominantSpeciesTile: React.FC<TileFetchProps> = ({ millCode, driveTime, onClick }) => {
  const { data, isLoading, error } = useSpeciesStats(millCode, driveTime);
  let value: string | null = null;
  if (data) {
    // First non-Other row is the most-abundant species by biomass share.
    const top = data.rows.find((r) => !r.isOther);
    if (top) {
      value = `${top.speciesName} ${Math.round(top.fractionOfBiomass * 100)}%`;
    }
  }
  return (
    <StatTile
      label="Dominant species"
      value={value}
      isLoading={isLoading}
      hasError={!!error}
      onClick={onClick}
    />
  );
};

// Yellow-accent CTA variant for the tree-count tile. Renders only on
// sheds with per-crown coverage (Sierra Nevada 60-min at v1.2). Spans
// both columns of the grid to read as the headline call-to-action —
// the per-tree drill-down is the dashboard's most-impressive feature
// and a regular stat tile buried it. Yellow accent matches the
// figma-yellow family used for Scenario Mode + Pilot-preview cues, so
// users register it as "guided / demo" not "production stat".
const TreeCountCtaTile: React.FC<TileFetchProps> = ({ millCode, driveTime, onClick }) => {
  const { data, isLoading, error } = usePerCrownStats(millCode, driveTime);
  const value = data ? compactNumber(data.treeCount) : null;
  const hasError = !!error;
  return (
    <button
      type="button"
      onClick={onClick}
      className="col-span-2 flex items-center justify-between gap-3 px-4 py-3 bg-figma-yellow/5 border border-figma-yellow/40 hover:border-figma-yellow focus:outline-none focus-visible:border-figma-yellow rounded transition-smooth text-left min-h-[68px]"
    >
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[10px] font-roboto-mono uppercase tracking-[0.10em] text-figma-yellow leading-tight">
          Trees attributed &middot; Sierra Nevada
        </span>
        {isLoading ? (
          <SkeletonBar />
        ) : hasError || value === null ? (
          <span className="text-[18px] font-roboto font-bold text-figma-ink-mute">—</span>
        ) : (
          <span className="text-[18px] font-roboto font-bold text-figma-text leading-tight">
            {value}
            <span className="ml-1 text-[12px] font-roboto font-normal text-figma-ink-mute">
              individual trees
            </span>
          </span>
        )}
      </div>
      <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-roboto-mono uppercase tracking-wider text-figma-yellow">
        <span>View on map</span>
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M2 6h8M7 3l3 3-3 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
};
